// Vendor Portal home — quick snapshot of empanelment status, pending
// documents, registration-fee state and relevant tender count.
import React, { useEffect, useState } from "react";
import { NavLink, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2, Circle, ChevronRight, CreditCard, FileText,
  ShieldCheck, TrendingUp, ExternalLink,
} from "lucide-react";
import vendorApi from "@/lib/vendorApi";
import { useVendor } from "@/pages/vendor/VendorLayout";
import { docChecklist } from "@/pages/vendor/vendorConfig";

function StatCard({ testid, icon: Icon, label, value, sublabel, tone = "teal" }) {
  const tones = {
    teal:   "text-[#0D5C63] bg-[#0D5C63]/10",
    amber:  "text-[#c78e42] bg-[#c78e42]/10",
    green:  "text-[#0f7a45] bg-[#0f7a45]/10",
    red:    "text-[#B02020] bg-[#B02020]/10",
    slate:  "text-[#5B5B5B] bg-[#5B5B5B]/10",
  };
  return (
    <div data-testid={testid} className="bg-white border border-[#E9E2D2] rounded-xl p-4 flex items-start gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone] || tones.teal}`}><Icon size={16} /></div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-[#8a8478]">{label}</div>
        <div className="mt-0.5 font-serif text-xl md:text-2xl text-[#1A1A1A]">{value}</div>
        {sublabel && <div className="text-[11px] text-[#8a8478] mt-0.5 truncate" title={sublabel}>{sublabel}</div>}
      </div>
    </div>
  );
}

export default function VendorDashboard() {
  const { reg, payment, reload } = useVendor();
  const [tenderCount, setTenderCount] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    vendorApi.get("/vendor/tenders").then(({ data }) => setTenderCount((data.tenders || []).length)).catch(() => setTenderCount(0));
  }, []);

  // Handle CCAvenue return banner
  useEffect(() => {
    const status = searchParams.get("payment");
    if (!status) return;
    const order = searchParams.get("order") || "";
    const messages = {
      success:   ["success", `Payment successful. Order ${order} recorded.`],
      failed:    ["error",   "Payment did not complete. Please try again."],
      cancelled: ["info",    "Payment was cancelled."],
      mismatch:  ["error",   "Amount or currency mismatch — payment held for review."],
      error:     ["error",   "We could not confirm the payment. Please contact support if amount was debited."],
    };
    const m = messages[status];
    if (m) {
      if (m[0] === "success") toast.success(m[1]);
      else if (m[0] === "info") toast.info(m[1]);
      else toast.error(m[1]);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("payment"); next.delete("order");
    setSearchParams(next, { replace: true });
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checklist = docChecklist(reg);
  const done = checklist.filter((c) => c.done).length;
  const pending = checklist.filter((c) => !c.done);
  const feeAmount = payment?.fee?.amount ?? 0;
  const feeWaived = !!payment?.fee?.is_waived;
  const isPaid = !!payment?.is_paid;
  const feeValue = feeWaived ? "Waived" : (isPaid ? "Cleared" : `₹ ${Number(feeAmount).toLocaleString("en-IN")}`);
  const feeTone  = feeWaived || isPaid ? "green" : "amber";

  return (
    <div className="p-5 md:p-8 max-w-6xl mx-auto space-y-6" data-testid="vendor-dashboard">
      {/* Firm header */}
      <section className="bg-white border border-[#E9E2D2] rounded-2xl p-5 md:p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#F26419] font-bold">Empanelment</div>
            <h1 className="trust-serif text-2xl md:text-3xl mt-1 text-[#1A1A1A]" data-testid="vendor-firm-name">{reg.applicant_firm_name || "—"}</h1>
            <div className="text-xs text-[#5B5B5B] mt-1">
              {[reg.city, reg.state].filter(Boolean).join(", ")}{reg.pin_code ? ` · ${reg.pin_code}` : ""}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-[#5B5B5B]">Status</div>
            <div className="mt-1 inline-flex items-center gap-1.5 bg-[#0f7a45]/10 text-[#0f7a45] text-xs font-bold px-3 py-1.5 rounded-full">
              <CheckCircle2 size={13} /> {reg.status || "Approved"}
            </div>
          </div>
        </div>
        <div className="mt-4 grid md:grid-cols-4 gap-3 text-xs">
          <div><div className="text-[10px] uppercase tracking-widest text-[#8a8478]">Type</div><div className="text-[#1A1A1A]">{reg.company_type || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#8a8478]">Categories</div><div className="text-[#1A1A1A]">{(reg.company_categories || []).join(", ") || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#8a8478]">Annual Turnover</div><div className="text-[#1A1A1A]">{reg.annual_turnover || "—"}</div></div>
          <div><div className="text-[10px] uppercase tracking-widest text-[#8a8478]">PAN</div><div className="text-[#1A1A1A]">{reg.pan_number || "—"}</div></div>
        </div>
      </section>

      {/* Stat tiles */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard testid="stat-docs" icon={ShieldCheck} label="Documents Uploaded" value={`${done}/6`} sublabel={done === 6 ? "All documents on file" : `${pending.length} pending`} tone={done === 6 ? "green" : "amber"} />
        <StatCard testid="stat-fee"  icon={CreditCard}  label="Registration Fee"    value={feeValue}     sublabel={payment?.paid_at ? `Paid ${new Date(payment.paid_at).toLocaleDateString()}` : (feeWaived ? "No fee currently charged" : "Awaiting payment")} tone={feeTone} />
        <StatCard testid="stat-tenders" icon={TrendingUp} label="Relevant Tenders"  value={tenderCount === null ? "…" : tenderCount} sublabel="Matching your registered categories" tone="teal" />
      </section>

      {/* Pending documents */}
      <section className="bg-white border border-[#E9E2D2] rounded-2xl p-5 md:p-6" data-testid="dash-pending-docs">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="trust-eyebrow text-[10px] text-[#0D5C63] font-bold uppercase tracking-widest">Pending Documents</div>
            <p className="text-xs text-[#8a8478] mt-0.5">Upload the items below to unlock the registration payment.</p>
          </div>
          <NavLink to="/vendor/portal/documents" className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-[#0D5C63] hover:text-[#F26419]">Upload <ChevronRight size={12} /></NavLink>
        </div>
        <ul className="mt-4 divide-y divide-[#efece6]">
          {checklist.map((c) => (
            <li key={c.key} className="py-2.5 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {c.done ? <CheckCircle2 size={16} className="text-[#0f7a45]" /> : <Circle size={16} className="text-[#c78e42]" />}
                <span className={`text-sm ${c.done ? "text-[#3E3E3E]" : "text-[#1A1A1A] font-medium"}`}>{c.title}</span>
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider ${c.done ? "text-[#0f7a45]" : "text-[#c78e42]"}`}>{c.done ? "Uploaded" : "Pending"}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Recent payment / relevant tenders shortcuts */}
      <section className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E9E2D2] rounded-2xl p-5" data-testid="dash-payment-shortcut">
          <div className="flex items-center gap-2 text-[#0D5C63]">
            <CreditCard size={16} />
            <h3 className="text-sm font-semibold">Registration Fee</h3>
          </div>
          {isPaid ? (
            <p className="text-xs text-[#3E3E3E] mt-2">Fee cleared{payment.paid_at ? ` on ${new Date(payment.paid_at).toLocaleDateString()}` : ""}.
              {payment.payment_order_id && payment.payment_order_id !== "WAIVED" && (
                <span className="block text-[10px] font-mono text-[#8a8478] mt-1">Order {payment.payment_order_id}</span>
              )}
            </p>
          ) : feeWaived ? (
            <p className="text-xs text-[#3E3E3E] mt-2">GS Mandal has waived the empanelment fee. Confirm on the payment page to finalise.</p>
          ) : (
            <p className="text-xs text-[#3E3E3E] mt-2">Pay ₹ {Number(feeAmount).toLocaleString("en-IN")} via CCAvenue once all documents are uploaded.</p>
          )}
          <NavLink to="/vendor/portal/payment" data-testid="dash-goto-payment" className="mt-3 inline-flex items-center gap-1.5 bg-[#0D5C63] hover:bg-[#083E43] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-full">
            {isPaid ? "View receipt" : (feeWaived ? "Confirm & finalise" : "Pay now")} <ChevronRight size={12} />
          </NavLink>
        </div>

        <div className="bg-white border border-[#E9E2D2] rounded-2xl p-5" data-testid="dash-tenders-shortcut">
          <div className="flex items-center gap-2 text-[#0D5C63]">
            <FileText size={16} />
            <h3 className="text-sm font-semibold">Relevant Tenders</h3>
          </div>
          <p className="text-xs text-[#3E3E3E] mt-2">
            {tenderCount === null ? "Loading…" : (tenderCount > 0
              ? `${tenderCount} live tender${tenderCount === 1 ? "" : "s"} matching your registered categories.`
              : "No live tenders matching your categories at the moment — check back later.")}
          </p>
          <NavLink to="/vendor/portal/tenders" data-testid="dash-goto-tenders" className="mt-3 inline-flex items-center gap-1.5 bg-white border border-[#0D5C63] text-[#0D5C63] hover:bg-[#0D5C63] hover:text-white text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-full transition-colors">
            View tenders <ExternalLink size={12} />
          </NavLink>
        </div>
      </section>
    </div>
  );
}
